/**
 * useThumbnail hook
 *
 * Lazily fetches a diagram's thumbnail SVG only once its card scrolls into
 * view, exposes it as an object URL for an `<img>`, and keeps the browser tidy:
 *
 * - Lazy loading uses an `IntersectionObserver` on a ref the card attaches to
 *   its root element, so off-screen cards never fetch (Requirement 9.6).
 * - Thumbnails are cached in a module-level `Map` keyed by Diagram_Id and
 *   Version for the session, so re-rendering the grid, switching views, or
 *   scrolling a card back into view reuses the already-fetched blob rather than
 *   refetching it (Requirement 9.6). A new Version produces a new cache key, so
 *   a saved diagram's thumbnail refreshes on its own.
 * - Object URLs created for a fetched blob are revoked when the hook unmounts,
 *   so leaving the browser does not leak them. Cached blobs themselves are kept
 *   for the session; only the per-mount object URLs are revoked.
 *
 * The thumbnail route (`getThumbnail`) returns the current file with the
 * embedded scene stripped; it takes no Version, so the Version here is used
 * purely as a cache key, not sent to the backend.
 */

import { useEffect, useRef, useState } from "react"
import { getThumbnail } from "@/services/diagramsService"

/**
 * The loading state of a single diagram's thumbnail, as returned by the hook.
 */
export interface ThumbnailState {
  /**
   * The object URL for the fetched thumbnail blob, or null while the thumbnail
   * has not loaded yet (not in view, in flight, or failed).
   */
  objectUrl: string | null
  /** True while the fetch is in flight. */
  isLoading: boolean
  /** The error if the fetch failed, otherwise null. */
  error: Error | null
}

/**
 * The session-scoped thumbnail cache, keyed by `${id}@${version}`. Caching the
 * Blob (not an object URL) lets each mount create and revoke its own object URL
 * from the shared blob, so revoking one card's URL never breaks another's.
 */
const thumbnailBlobCache: Map<string, Blob> = new Map<string, Blob>()

/**
 * Build the session cache key for a diagram thumbnail.
 *
 * @param params - The key inputs.
 * @param params.id - The Diagram_Id.
 * @param params.version - The Version (last commit ID) the thumbnail is for.
 * @returns The cache key combining the id and Version.
 */
function thumbnailCacheKey({ id, version }: { id: string; version: string }): string {
  return `${id}@${version}`
}

/**
 * Lazily load a diagram's thumbnail when its card enters the viewport.
 *
 * Attach the returned `ref` to the element whose visibility should trigger the
 * fetch (typically the card root). Once that element intersects the viewport,
 * the hook fetches the thumbnail (or reuses the cached blob for this id and
 * Version), exposes an object URL, and revokes that URL on unmount.
 *
 * @param params - The hook inputs.
 * @param params.id - The Diagram_Id whose thumbnail to load.
 * @param params.version - The current Version, used as part of the cache key so
 *   a saved diagram's thumbnail refreshes when its Version changes.
 * @param params.getAccessToken - Returns a fresh Auth0 access token for the
 *   authenticated thumbnail request (from `useAuth`).
 * @returns A ref callback to attach to the observed element and the thumbnail
 *   loading state (`objectUrl`, `isLoading`, `error`).
 */
export function useThumbnail({
  id,
  version,
  getAccessToken,
}: {
  id: string
  version: string
  getAccessToken: () => Promise<string>
}): { ref: (node: HTMLElement | null) => void; state: ThumbnailState } {
  const [state, setState] = useState<ThumbnailState>({
    objectUrl: null,
    isLoading: false,
    error: null,
  })

  // Whether the observed element has entered the viewport at least once. Once
  // visible, it stays "triggered" so the thumbnail is not dropped when the card
  // scrolls back out of view.
  const [isInView, setIsInView] = useState<boolean>(false)

  // The element currently observed. A ref callback (rather than a ref object)
  // lets us (dis)connect the observer exactly when the node mounts/unmounts.
  const observedNodeRef = useRef<HTMLElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  /**
   * Ref callback attached to the observed element. Disconnects any previous
   * observer and starts observing the new node (if any) for viewport entry.
   *
   * @param node - The element to observe, or null when it unmounts.
   */
  const ref = (node: HTMLElement | null): void => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    observedNodeRef.current = node
    if (node === null) {
      return
    }
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        // A single element is observed, so the first entry is the only entry.
        if (entries.length > 0 && entries[0].isIntersecting) {
          setIsInView(true)
          observer.disconnect()
          observerRef.current = null
        }
      },
      // A small root margin starts the fetch just before the card is fully
      // on-screen, so thumbnails are usually ready by the time they are seen.
      { rootMargin: "200px" },
    )
    observer.observe(node)
    observerRef.current = observer
  }

  useEffect(() => {
    if (!isInView) {
      return
    }

    // Tracks this effect run so a late-arriving fetch for a stale id/version
    // (after the deps changed) does not overwrite the current state.
    let isActive: boolean = true
    // The object URL created for this mount, revoked on cleanup.
    let createdObjectUrl: string | null = null

    const cacheKey: string = thumbnailCacheKey({ id, version })

    /**
     * Turn a thumbnail blob into an object URL and publish it to state,
     * remembering the URL so cleanup can revoke it.
     *
     * @param blob - The thumbnail SVG blob.
     */
    const publishBlob = (blob: Blob): void => {
      if (!isActive) {
        return
      }
      createdObjectUrl = URL.createObjectURL(blob)
      setState({ objectUrl: createdObjectUrl, isLoading: false, error: null })
    }

    const cachedBlob: Blob | undefined = thumbnailBlobCache.get(cacheKey)
    if (cachedBlob !== undefined) {
      publishBlob(cachedBlob)
    } else {
      setState({ objectUrl: null, isLoading: true, error: null })
      void (async (): Promise<void> => {
        try {
          const token: string = await getAccessToken()
          const blob: Blob = await getThumbnail(token, id)
          // Cache the blob for the session before publishing, so other mounts
          // of the same id/version reuse it.
          thumbnailBlobCache.set(cacheKey, blob)
          publishBlob(blob)
        } catch (caught: unknown) {
          if (!isActive) {
            return
          }
          setState({
            objectUrl: null,
            isLoading: false,
            error: caught instanceof Error ? caught : new Error("Failed to load thumbnail"),
          })
        }
      })()
    }

    return (): void => {
      isActive = false
      if (createdObjectUrl !== null) {
        URL.revokeObjectURL(createdObjectUrl)
      }
    }
  }, [isInView, id, version, getAccessToken])

  return { ref, state }
}
